import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import _ from 'lodash';
import { AlertOctagon } from 'lucide-react';
import { type PropsWithChildren, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { formatCurrencyValue } from '../../utils';
import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';

import { Security } from './Security';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import NumberField from '@/components/NumberField';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex min-w-0 items-center gap-2 text-sm">
    <span className="shrink-0 text-sm">{label}</span>
    <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
      {value}
    </TruncatedTextTooltip>
  </div>
);

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{
  title: string;
}>) => (
  <div className="mb-2 flex h-9 items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

// Security type key to display name mapping
const SECURITY_TYPE_LABELS: Record<string, string> = {
  capitalized_leases: 'Capitalized Leases',
  cash: 'Cash',
  common_equity: 'Common Equity',
  convertible_preferred_equity: 'Convertible Preferred Equity',
  deffered_consideration: 'Deferred Consideration',
  delayed_draw_term_loan: 'Delayed Draw Term Loan',
  earnout: 'Earnout',
  equipment_line: 'Equipment Line',
  first_lien_term_loan: 'First Lien Term Loan',
  first_lien_term_loan_first_out: 'First Lien Term Loan (First Out)',
  first_lien_term_loan_last_out: 'First Lien Term Loan (Last Out)',
  holdco_note: 'HoldCo Note',
  implied_equity: 'Implied Equity',
  management_incentive_plan: 'Management Incentive Plan',
  non_participating_preferred_equity: 'Non-Participating Preferred Equity',
  participating_preferred_equity: 'Participating Preferred Equity',
  redeemable_preferred_equity: 'Redeemable Preferred Equity',
  revolver: 'Revolver',
  rollover_equity: 'Rollover Equity',
  second_lien_term_loan: 'Second Lien Term Loan',
  seller_note: 'Seller Note',
  unsecured_note: 'Unsecured Note',
  warrants: 'Warrants'
};

// Helper function to get security type display name
const getSecurityTypeLabel = (key: null | string | undefined): string => {
  if (!key) return '-';
  return SECURITY_TYPE_LABELS[key] ?? _.startCase(key);
};

// Helper function to format any selection field value for display
const formatSelectionLabel = (key: null | string | undefined): string => {
  if (!key) return '-';
  return SECURITY_TYPE_LABELS[key] ?? _.startCase(key);
};
interface GetDealOpportunityCaptable {
  node: {
    __typename: 'OpportunityNode';
    computedProjectedDebtIrr: null | number;
    computedProjectedDebtMoic: null | number;
    computedProjectedEquityIrr: null | number;
    computedProjectedEquityMoic: null | number;
    computedProjectedTotalIrr: null | number;
    computedProjectedTotalMoic: null | number;
    computedSecuritySummary:
      | {
          amount: null | number;
          category: string;
          cumulativeAmount?: number;
          id: string;
          lien?: number;
          netAttachmentPoint: string;
          netDetachmentPoint: string;
          netMultiple: string;
          ownership_percentage?: number;
          party?: string;
          party_type?: string;
          percentOfTotal: number;
          security_id: null | number;
          security_type_key: string;
          security_type_name: string;
          type?: string;
        }[]
      | null;
    computedSourcesUses: {
      sources: {
        amount: null | number;
        cash_noncash?: string;
        id?: null | number;
        lien?: number;
        party?: null | string;
        security_type_key: null | string;
        security_type_name?: null | string;
      }[];
      uses: {
        amount: number;
        cash_noncash?: string;
        cash_uses: string;
      }[];
    } | null;
    id: string;
    ltmEbitda: null | number;
    name: null | string;
    projectedDebtIrr: null | number;
    projectedDebtMoic: null | number;
    projectedEquityIrr: null | number;
    projectedEquityMoic: null | number;
    projectedTotalIrr: null | number;
    projectedTotalMoic: null | number;
    referencePoint: null | string;
  };
}

const GET_DEAL_OPPORTUNITY_CAPTABLE = gql`
  query DealOpportunity_CapTable($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        ltmEbitda
        referencePoint
        computedSecuritySummary
        computedSourcesUses
        computedProjectedDebtIrr
        computedProjectedDebtMoic
        computedProjectedEquityIrr
        computedProjectedEquityMoic
        computedProjectedTotalIrr
        computedProjectedTotalMoic
        projectedDebtIrr
        projectedDebtMoic
        projectedEquityIrr
        projectedEquityMoic
        projectedTotalIrr
        projectedTotalMoic
      }
    }
  }
`;

interface GetOpportunitySecurities {
  opportunitySecurities: {
    __typename: 'DealOpportunitySecurityNode';
    amount: null | number;
    callProtection: null | string;
    category: null | string;
    hideFromCapTable: boolean | null;
    hideFromSources: boolean | null;
    id: string;
    impliedLoRate: null | number;
    leverageBasedPricing: null | number;
    lien: null | number;
    name: null | string;
    securityType: null | string;
    totalAmount: null | number;
    unitranchInterestRate: null | number;
  }[];
}

const GET_OPPORTUNITY_SECURITIES = gql`
  query DOSecurity($deal_id: ID!) {
    opportunitySecurities(dealOpportunityId: $deal_id) {
      id
      name
      securityType
      category
      amount
      lien
      totalAmount
      hideFromCapTable
      hideFromSources
      callProtection
      unitranchInterestRate
      impliedLoRate
      leverageBasedPricing
    }
  }
`;

const UPDATE_OPPORTUNITY = gql`
  mutation UpdateDealOpportunity_CapTable(
    $ltm_ebitda: Float
    $reference_point: String
    $projected_debt_irr: Float
    $projected_debt_moic: Float
    $projected_equity_irr: Float
    $projected_equity_moic: Float
    $projected_total_irr: Float
    $projected_total_moic: Float
    $id: ID!
  ) {
    updateOpportunity(
      id: $id
      values: {
        ltmEbitda: $ltm_ebitda
        referencePoint: $reference_point
        projectedDebtIrr: $projected_debt_irr
        projectedDebtMoic: $projected_debt_moic
        projectedEquityIrr: $projected_equity_irr
        projectedEquityMoic: $projected_equity_moic
        projectedTotalIrr: $projected_total_irr
        projectedTotalMoic: $projected_total_moic
      }
    ) {
      ok
    }
  }
`;

const Wrapper = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    throw new Error();
  }

  const { data: data1, loading: loading1 } = useQuery<
    GetDealOpportunityCaptable,
    { id: string }
  >(GET_DEAL_OPPORTUNITY_CAPTABLE, {
    variables: { id }
  });

  const { data: data2, loading: loading2 } = useQuery<
    GetOpportunitySecurities,
    { deal_id: string }
  >(GET_OPPORTUNITY_SECURITIES, {
    variables: { deal_id: id }
  });

  if (loading1 || loading2) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1 || !data2) {
    return <div>{'Error'}</div>;
  }

  return (
    <InvestmentsDirectIdTransactionStructure
      data1={data1}
      data2={data2}
      id={id}
    />
  );
};

interface Props {
  data1: GetDealOpportunityCaptable;
  data2: GetOpportunitySecurities;
  id: string;
}

const InvestmentsDirectIdTransactionStructure = ({
  data1,
  data2,
  id
}: Props) => {
  const [overviewDialogIsOpen, setOverviewDialogIsOpen] = useState(false);
  const [cashUseTotal, setCashUseTotal] = useState<null | number>(null);

  const [updateOpportunity, { loading: isUpdatingOpportunity }] =
    useMutation(UPDATE_OPPORTUNITY);

  const test1 = data1.node.computedSourcesUses?.sources.filter(
    s =>
      s.cash_noncash === 'cash' ||
      (s.security_type_key ?? '') === 'Total Sources (Cash)'
  );

  const test2 = data1.node.computedSourcesUses?.uses.filter(
    u =>
      u.cash_noncash === 'cash' || (u.cash_uses || '') === 'Total Uses (Cash)'
  );

  const test3 = data1.node.computedSourcesUses?.sources.filter(
    s =>
      (s.cash_noncash !== 'cash' &&
        (s.security_type_key ?? '') !== 'Total Sources (Cash)') ||
      (s.security_type_key ?? '') === 'Total Sources (Non-Cash)'
  );

  const test4 = data1.node.computedSourcesUses?.uses.filter(
    u =>
      (u.cash_noncash !== 'cash' &&
        (u.cash_uses || '') !== 'Total Uses (Cash)') ||
      (u.cash_uses || '') === 'Total Uses (Non-Cash)'
  );

  const handleOverviewFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const ebitda = formData.get('ebitda-field');
    const debtIrr = formData.get('debt-irr-0%-field');
    const debtMoic = formData.get('debt-moic-0x-field');
    const equityIrr = formData.get('equity-irr-0%-field');
    const equityMoic = formData.get('equity-moic-0x-field');
    const totalIrr = formData.get('total-irr-0%-field');
    const totalMoic = formData.get('total-moic-0x-field');
    const referencePoint = formData.get('reference-point-field');

    try {
      const { error } = await updateOpportunity({
        refetchQueries: [
          {
            query: GET_DEAL_OPPORTUNITY_CAPTABLE,
            variables: { id }
          }
        ],
        variables: {
          id,
          ltm_ebitda: ebitda ? +ebitda : null,
          projected_debt_irr: debtIrr ? +debtIrr / 100 : null,
          projected_debt_moic: debtMoic ? +debtMoic : null,
          projected_equity_irr: equityIrr ? +equityIrr / 100 : null,
          projected_equity_moic: equityMoic ? +equityMoic : null,
          projected_total_irr: totalIrr ? +totalIrr / 100 : null,
          projected_total_moic: totalMoic ? +totalMoic : null,
          reference_point: referencePoint === '' ? null : referencePoint
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setOverviewDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="@container min-w-0 space-y-6 px-5 pt-5 pb-16.5 md:pl-14">
          <div>
            <div className="mb-2.5 flex items-center justify-between gap-x-2">
              <h2 className="text-lg font-semibold">Overview</h2>
              <div className="flex items-center gap-x-2">
                {!data1.node.ltmEbitda && (
                  <Alert className="border-amber-300 bg-amber-50 text-amber-500">
                    <AlertOctagon />
                    <AlertTitle>
                      EBITDA has not been populated. Please do so below.
                    </AlertTitle>
                  </Alert>
                )}
                <Dialog
                  onOpenChange={setOverviewDialogIsOpen}
                  open={overviewDialogIsOpen}
                >
                  <DialogTrigger asChild>
                    <EditButton />
                  </DialogTrigger>
                  <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                      <div className="flex items-center gap-x-2">
                        <DialogTitle>Overview</DialogTitle>
                        <Badge variant="yellow">Unsaved</Badge>
                      </div>
                    </DialogHeader>
                    <DialogBody>
                      <form
                        id="overview-form"
                        noValidate
                        onSubmit={event => void handleOverviewFormSubmit(event)}
                      >
                        <FieldGroup>
                          <FieldGroup className="grid grid-cols-2">
                            <Field>
                              <FieldLabel htmlFor="reference-point-field">
                                Reference Point
                              </FieldLabel>
                              <Input
                                defaultValue={data1.node.referencePoint ?? ''}
                                id="reference-point-field"
                                name="reference-point-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="ebitda-field">
                                EBITDA
                              </FieldLabel>
                              <InputGroup>
                                <InputGroupAddon>
                                  <InputGroupText>$</InputGroupText>
                                </InputGroupAddon>
                                <NumberField
                                  defaultValue={
                                    data1.node.ltmEbitda ?? undefined
                                  }
                                  id="ebitda-field"
                                  name="ebitda-field"
                                  placeholder="Enter Value"
                                />
                              </InputGroup>
                            </Field>
                          </FieldGroup>
                          <FieldSet>
                            <FieldLegend>IRR</FieldLegend>
                            <FieldGroup className="grid grid-cols-2">
                              <Field>
                                <FieldLabel htmlFor="debt-irr-0%-field">
                                  Debt IRR (0%)
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    data1.node.projectedDebtIrr != null
                                      ? data1.node.projectedDebtIrr * 100
                                      : undefined
                                  }
                                  id="debt-irr-0%-field"
                                  name="debt-irr-0%-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="equity-irr-0%-field">
                                  Equity IRR (0%)
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    data1.node.projectedEquityIrr != null
                                      ? data1.node.projectedEquityIrr * 100
                                      : undefined
                                  }
                                  id="equity-irr-0%-field"
                                  name="equity-irr-0%-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="total-irr-0%-field">
                                  Total IRR (0%)
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    data1.node.projectedTotalIrr != null
                                      ? data1.node.projectedTotalIrr * 100
                                      : undefined
                                  }
                                  id="total-irr-0%-field"
                                  name="total-irr-0%-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                            </FieldGroup>
                          </FieldSet>
                          <FieldSet>
                            <FieldLegend>MOIC</FieldLegend>
                            <FieldGroup className="grid grid-cols-2">
                              <Field>
                                <FieldLabel htmlFor="debt-moic-0x-field">
                                  Debt MOIC (0x)
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    data1.node.projectedDebtMoic ?? undefined
                                  }
                                  id="debt-moic-0x-field"
                                  name="debt-moic-0x-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="equity-moic-0x-field">
                                  Equity MOIC (0x)
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    data1.node.projectedEquityMoic ?? undefined
                                  }
                                  id="equity-moic-0x-field"
                                  name="equity-moic-0x-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="total-moic-0x-field">
                                  Total MOIC (0x)
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    data1.node.projectedTotalMoic ?? undefined
                                  }
                                  id="total-moic-0x-field"
                                  name="total-moic-0x-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                            </FieldGroup>
                          </FieldSet>
                        </FieldGroup>
                      </form>
                    </DialogBody>
                    <DialogFooter>
                      <Button
                        disabled={isUpdatingOpportunity}
                        form="overview-form"
                        type="submit"
                      >
                        {isUpdatingOpportunity && <Spinner />}
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div className="bg-card @container space-y-5 rounded-md border p-5">
              <div className="grid gap-x-6 gap-y-3 @md:grid-cols-2">
                <InfoRow
                  label="Reference Point"
                  value={data1.node.referencePoint ?? '-'}
                />
                <InfoRow
                  label="EBITDA"
                  value={
                    data1.node.ltmEbitda
                      ? formatCurrencyValue(data1.node.ltmEbitda)
                      : '-'
                  }
                />
              </div>
              <h3 className="border-b pb-2.5 font-medium">IRR</h3>
              <div className="grid gap-x-6 gap-y-3 @md:grid-cols-2">
                <InfoRow
                  label="Debt IRR (0%)"
                  value={
                    data1.node.projectedDebtIrr != null
                      ? `${(data1.node.projectedDebtIrr * 100).toFixed(2)}%`
                      : '-'
                  }
                />
                <InfoRow
                  label="Equity IRR (0%)"
                  value={
                    data1.node.projectedEquityIrr != null
                      ? `${(data1.node.projectedEquityIrr * 100).toFixed(2)}%`
                      : '-'
                  }
                />
                <InfoRow
                  label="Total IRR (0%)"
                  value={
                    data1.node.projectedTotalIrr != null
                      ? `${(data1.node.projectedTotalIrr * 100).toFixed(2)}%`
                      : '-'
                  }
                />
              </div>
              <h3 className="border-b pb-2.5 font-medium">MOIC</h3>
              <div className="grid gap-x-6 gap-y-3 @md:grid-cols-2">
                <InfoRow
                  label="Debt MOIC (0x)"
                  value={data1.node.projectedDebtMoic?.toString() ?? '-'}
                />
                <InfoRow
                  label="Equity MOIC (0x)"
                  value={data1.node.projectedEquityMoic?.toString() ?? '-'}
                />
                <InfoRow
                  label="Total MOIC (0x)"
                  value={data1.node.projectedTotalMoic?.toString() ?? '-'}
                />
              </div>
            </div>
          </div>
          {cashUseTotal !== null &&
            !(
              _.sum(data2.opportunitySecurities.map(x => x.amount)) ===
              cashUseTotal
            ) && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-500">
                <AlertOctagon />
                <AlertTitle>
                  The entered sources do not match the security amount totals.
                  Please confirm and re-enter the correct amounts.
                </AlertTitle>
              </Alert>
            )}
          <div className="grid gap-4 @md:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <SectionHeader title="Cash Source" />
              <div className="bg-card rounded-md border p-4">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">
                        Cash Sources
                      </TableHead>
                      <TableHead className="text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {test1?.map(a => (
                      <TableRow key={a.security_type_key} className="h-10">
                        <TableCell className="text-sm font-medium">
                          {getSecurityTypeLabel(a.security_type_key)}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {a.amount ? formatCurrencyValue(a.amount) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="min-w-0 space-y-2">
              <SectionHeader title="Cash Uses" />
              <div className="bg-card rounded-md border p-4">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">
                        Cash Uses
                      </TableHead>
                      <TableHead className="text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {test2?.map(a => (
                      <TableRow key={a.cash_uses} className="h-10">
                        <TableCell className="text-sm font-medium">
                          {a.cash_uses}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {a.amount ? formatCurrencyValue(a.amount) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <SectionHeader title="Non-Cash Sources" />
            <div className="bg-card rounded-md border p-4">
              <Table className="text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold">
                      Source
                    </TableHead>
                    <TableHead className="text-xs font-semibold">ID</TableHead>
                    <TableHead className="text-xs font-semibold">
                      Security Type Name
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Party
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Lien
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Cash/Non-Cash
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(test3 ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell
                        className="text-muted-foreground text-sm"
                        colSpan={3}
                      >
                        No non-cash sources.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (test3 ?? []).map(source => (
                      <TableRow key={source.id}>
                        <TableCell className="text-sm font-medium">
                          {getSecurityTypeLabel(source.security_type_key)}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {source.id ?? ''}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {source.security_type_name ?? ''}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {source.party ?? ''}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {source.lien ?? ''}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {source.cash_noncash
                            ? formatSelectionLabel(source.cash_noncash)
                            : ''}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="space-y-2">
            <SectionHeader title="Non-Cash Uses" />
            <div className="bg-card rounded-md border p-4">
              <Table className="text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold">Use</TableHead>
                    <TableHead className="text-xs font-semibold">
                      Cash/Non-Cash
                    </TableHead>
                    <TableHead className="text-right text-xs font-semibold">
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(test4 ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell
                        className="text-muted-foreground text-sm"
                        colSpan={3}
                      >
                        No non-cash uses.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (test4 ?? []).map((use, index) => (
                      <TableRow key={`${use.cash_uses}-${index.toString()}`}>
                        <TableCell className="text-sm font-medium">
                          {use.cash_uses}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {use.cash_noncash
                            ? formatSelectionLabel(use.cash_noncash)
                            : ''}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          {use.amount ? formatCurrencyValue(use.amount) : ''}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          <Security
            id={id}
            onCashUseTotalChange={setCashUseTotal}
          />
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default Wrapper;
